import {
  EnvironmentInjector,
  inject,
  Injectable,
  runInInjectionContext,
} from '@angular/core';
import { where } from '@angular/fire/firestore';
import {
  Category,
  CompetitionType,
  LicenseRateSeason,
  LicenseType,
  PlayerRanking,
  Scope,
  Season,
  SeasonLeague,
  UserGender,
} from '@models';
import { Timestamp } from '@angular/fire/firestore';
import { docData, getDoc } from '@angular/fire/firestore';
import { convertTimestampsToDates, getCategoryFromBirthDate } from '@utils';
import { addDays } from 'date-fns';
import {
  catchError,
  combineLatest,
  defer,
  distinctUntilChanged,
  firstValueFrom,
  from,
  map,
  Observable,
  of,
  ReplaySubject,
  share,
  shareReplay,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import { AuthService } from './auth.service';
import { FirestoreService } from './firestore.service';
import { SeasonManagerService } from './season-manager.service';
import { SportContextService } from './sport-context.service';
import { TenantContextService } from './tenant-context.service';
import { UsersService } from './user.service';

/**
 * How long a by-id read of a season outside `all$` keeps its listener after
 * the last subscriber leaves (see `SeasonsService.getOutsideSelection`).
 */
const OUTSIDE_SELECTION_KEEPALIVE_MS = 30_000;

@Injectable({
  providedIn: 'root',
})
export class SeasonsService {
  public all$: Observable<Season[]>;
  public seasonsWithActivatedTeamInscription$: Observable<Season[]>;

  private readonly injector = inject(EnvironmentInjector);
  private readonly authService = inject(AuthService);
  /**
   * By-id reads of seasons that are not in `all$`, keyed by document path
   * (the path carries the tenant). See `getOutsideSelection()`.
   */
  private readonly outsideSelection = new Map<
    string,
    Observable<Season | undefined>
  >();

  constructor(
    private firestore: FirestoreService,
    private seasonManager: SeasonManagerService,
    private tenantContext: TenantContextService,
    private usersService: UsersService,
    private sportContext: SportContextService,
  ) {
    this.all$ = combineLatest([
      this.tenantContext.tenantId$,
      this.seasonManager.season$,
    ]).pipe(
      distinctUntilChanged(([ta, a], [tb, b]) => ta === tb && a === b),
      switchMap(([, season]) => this.listByYear(season)),
      shareReplay({ bufferSize: 1, refCount: true }),
    );

    this.seasonsWithActivatedTeamInscription$ = this.all$.pipe(
      map((seasons: Season[]) => {
        const now = new Date();
        return seasons.filter((season) => {
          if (
            !season.inscriptionTeamsStartDate ||
            !season.inscriptionTeamsEndDate
          ) {
            return false;
          }
          const startDate = season.inscriptionTeamsStartDate;
          const endDate = addDays(season.inscriptionTeamsEndDate, 1);
          return startDate && endDate && startDate <= now && endDate >= now;
        });
      }),
    );
  }

  private get col() {
    return this.tenantContext.collections.SEASONS;
  }

  private get ratesCol() {
    return this.tenantContext.collections.SEASON_RATES;
  }

  /**
   * Seasons of `seasonYear` (and of the active sport in a multi-sport tenant).
   * Same query as `all$`, but for an explicit year instead of the selected one:
   * it does not read nor change `SeasonManagerService.season$`.
   */
  listByYear(seasonYear: string): Observable<Season[]> {
    return combineLatest([
      this.tenantContext.tenantId$,
      this.sportContext.activeSport$,
      this.sportContext.isMultiSport$,
    ]).pipe(
      distinctUntilChanged(
        ([ta, sa, ma], [tb, sb, mb]) => ta === tb && sa === sb && ma === mb,
      ),
      switchMap(([, activeSport, isMultiSport]) => {
        const constraints = [where('seasonYear', '==', seasonYear)];
        if (isMultiSport && activeSport) {
          constraints.push(where('sport', '==', activeSport));
        }
        return this.firestore.listDocuments<Season>(
          this.tenantContext.collections.SEASONS,
          ...constraints,
        );
      }),
    );
  }

  /**
   * Season by id, whatever the selected season year.
   *
   * Looks it up in `all$` first (same object and same synchronous emission as
   * before). When it is not there — typically because it belongs to another
   * `seasonYear` — it falls back to reading the document by id.
   *
   * Emits `undefined` (despite the historical `Observable<Season>` type) when
   * the document does not exist, is soft-deleted, cannot be read, or belongs
   * to another sport in a multi-sport tenant (the sport filter of `all$` is
   * kept; only the year filter is dropped).
   */
  get(id: string): Observable<Season> {
    return this.all$.pipe(
      switchMap((seasons) => {
        const found = seasons.find((c) => c.id === id);
        return found ? of(found) : this.getOutsideSelection(id);
      }),
    ) as Observable<Season>;
  }

  /**
   * Live by-id read used by `get()` when the season is not in `all$`.
   *
   * - `docData` directly (not `FirestoreService.getDocument`, which never emits
   *   for a missing document and would hang every `firstValueFrom(get(id))`).
   * - Same shape as `all$`: dates converted, `deleted` docs dropped, same sport
   *   filter.
   * - Cached per path and shared with a delayed reset: the impure `season` pipe
   *   re-subscribes on every change detection, so the stream must survive a
   *   momentary zero ref-count and replay synchronously, instead of opening a
   *   new listener each cycle.
   */
  private getOutsideSelection(id: string): Observable<Season | undefined> {
    const path = this.col;
    const key = `${path}/${id}`;
    let cached = this.outsideSelection.get(key);
    if (!cached) {
      const doc$ = defer(() =>
        runInInjectionContext(this.injector, () =>
          docData<Season>(this.firestore.getDocumentRef<Season>(path, id), {
            idField: 'id',
          }),
        ),
      ).pipe(
        catchError((err) => {
          console.error(`[SeasonsService] get(${id}) error:`, err);
          return of(undefined);
        }),
      );
      cached = combineLatest([
        doc$,
        this.sportContext.activeSport$,
        this.sportContext.isMultiSport$,
      ]).pipe(
        map(([season, activeSport, isMultiSport]) => {
          if (!season || season.deleted === true) return undefined;
          if (isMultiSport && activeSport && season.sport !== activeSport) {
            return undefined;
          }
          return convertTimestampsToDates(season);
        }),
        takeUntil(this.authService.onLogout),
        share({
          connector: () => new ReplaySubject<Season | undefined>(1),
          resetOnError: true,
          resetOnComplete: true,
          resetOnRefCountZero: () => timer(OUTSIDE_SELECTION_KEEPALIVE_MS),
        }),
      );
      this.outsideSelection.set(key, cached);
    }
    return cached;
  }

  getLeagues(scope: Scope) {
    return this.all$.pipe(
      map((seasons) =>
        seasons.filter(
          (s) => s.type === CompetitionType.LEAGUE && s.scope === scope,
        ),
      ),
    );
  }

  getLeague(docId: string) {
    return this.firestore.getDocument<SeasonLeague>(this.col, docId);
  }

  create(season: Partial<Season>) {
    const activeSport = this.sportContext.getActiveSport();
    return this.firestore.create<Partial<Season>>(this.col, {
      ...season,
      seasonYear: this.seasonManager.getSeason(),
      ...(!season.sport && activeSport ? { sport: activeSport } : {}),
    });
  }

  update(id: string, season: Partial<Season>) {
    return this.firestore
      .update<Season>(this.col, { id, ...season } as Season)
      .then(() => id);
  }

  createOrUpdate(id: string | undefined, season: Partial<Season>) {
    return id ? this.update(id, season) : this.create(season);
  }

  saveRates(seasonId: string, rates: LicenseRateSeason) {
    return this.firestore
      .createOrUpdate(this.ratesCol, seasonId, {
        id: seasonId,
        teamRates: rates.teamRates,
        licenseRates: rates.licenseRates,
      })
      .then(() => seasonId);
  }

  getRates(seasonId: string): Observable<LicenseRateSeason> {
    const ref = this.firestore.getDocumentRef<LicenseRateSeason>(
      this.ratesCol,
      seasonId,
    );
    return docData<LicenseRateSeason>(ref, { idField: 'id' }).pipe(
      map((data) => {
        return data ?? ({} as LicenseRateSeason);
      }),
      catchError((err) => {
        console.error('[SeasonsService] docData error:', err);
        return of({} as LicenseRateSeason);
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /**
   * Lectura ONE-SHOT y fresca del servidor de las tarifas de una temporada.
   * A diferencia de `getRates()` (docData/onSnapshot, que emite primero el valor
   * de la caché IndexedDB y puede estar desactualizado), `getDoc()` va al servidor
   * cuando hay conexión. Úsalo cuando el valor debe ser el más reciente (p.ej. el
   * cálculo del precio de inscripción), no un valor cacheado.
   */
  async getRatesOnce(seasonId: string): Promise<LicenseRateSeason> {
    const ref = this.firestore.getDocumentRef<LicenseRateSeason>(
      this.ratesCol,
      seasonId,
    );
    try {
      const snap = await getDoc(ref);
      return (snap.data() as LicenseRateSeason) ?? ({} as LicenseRateSeason);
    } catch (err) {
      console.error('[SeasonsService] getRatesOnce error:', err);
      return {} as LicenseRateSeason;
    }
  }

  async findLicenseRateForAPlayer(
    seasonId: string,
    birthDate: Date,
    gender: UserGender,
    licenseType: LicenseType,
  ) {
    try {
      const rates = await firstValueFrom(this.getRates(seasonId));
      if (!rates.licenseRates) {
        throw new Error('No hay tarifas definidas para la temporada');
      }
      const category = this.getCategory(birthDate);
      return rates.licenseRates
        .filter((data) => data.licenseType === licenseType)
        .filter((data) => data.genders.includes(gender))
        .filter((data) => data.categories.includes(category));
    } catch (error) {
      throw new Error('No hay tarifas definidas para la temporada');
    }
  }

  getReport() {
    return this.firestore.listDocuments<Season>(this.col);
  }

  getSeasonWithEnrichedPlayerRankings(seasonId: string): Observable<Season> {
    return this.get(seasonId).pipe(
      switchMap((season) => {
        const playerRankings = (season as SeasonLeague).playersRanking || [];
        if (!season.hasPlayerRanking || playerRankings.length === 0) {
          return from([season]);
        }
        // Public "follow the sport" screen: resolve player names/photos through
        // the public `getPlayersBasicInfo` callable (works without session)
        // instead of reading each `users/{id}` doc directly (member-only, PII).
        const playerIds = playerRankings.map((r) => r.playerId);
        return from(this.usersService.getPlayersBasicInfo(playerIds)).pipe(
          map((infos) => {
            const byId = new Map(infos.map((i) => [i.id, i]));
            const enriched = playerRankings.map((ranking) => ({
              ...ranking,
              userData: (byId.get(ranking.playerId) ??
                undefined) as unknown as PlayerRanking['userData'],
            }));
            return { ...season, playersRanking: enriched } as Season;
          }),
          catchError(() => from([season])),
        );
      }),
    );
  }

  private getCategory(birthDate: Date): Category {
    const d = birthDate instanceof Timestamp ? birthDate.toDate() : birthDate;
    return getCategoryFromBirthDate(d, this.seasonManager.getSeasonChangeMonth());
  }
}
